import { FC, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { SettingsCard } from "../../../../components/card/SettingsCard";
import { Button } from "../../../../components/newUI/button/Button";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  AUTOMATION_CONDITION_KEY,
  useAddDefaultAutomationMutation,
  useDeleteDefaultAutomationMutation,
  useGetAutomationConditionsQuery,
} from "../../../../slices/AutomationSlice";
import { AddAutomationModal } from "../../../work/pages/automation/AddAutomationModal";
import {
  AutomationDraft,
  AutomationItem,
} from "../../../work/pages/automation/automation.constants";
import {
  getAutomationActionBodyValue,
  getAutomationConditionBodyValue,
} from "../../../work/pages/automation/automation.helpers";
import { AutomationTarget } from "../../../work/pages/automation/automation.types";
import { AutomationCard } from "../../../work/pages/automation/AutomationCard";
import { useAutomationLookups } from "../../../work/pages/automation/useAutomationLookups";
import { mapWorkTypeDefaultAutomationsToItems } from "../model/workTypeAutomation.mappers";
import { WorkTypeAutomationSectionProps } from "../model/workTypeEditor.types";

const EMPTY_STATE_TEXT =
  "No automations yet. Add one and it runs automatically on new work of this type.";

export const WorkTypeAutomationSection: FC<WorkTypeAutomationSectionProps> = ({
  isVisible,
  workType,
  onRefresh,
}) => {
  const user = useAppSelector((state) => state.appReducer.user);
  const lookups = useAutomationLookups(workType?.id);
  const [addDefaultAutomation, { isLoading: isAddingAutomation }] =
    useAddDefaultAutomationMutation();
  const [deleteDefaultAutomation] = useDeleteDefaultAutomationMutation();
  const {
    data: conditions = [],
    isFetching: isLoadingConditions,
    isError: isConditionsError,
  } = useGetAutomationConditionsQuery(undefined, { skip: !isVisible });

  const [selectedTarget, setSelectedTarget] = useState<AutomationTarget | null>(
    null,
  );
  const isAddingAutomationRef = useRef(false);
  const deletingAutomationIdsRef = useRef<Set<string>>(new Set());
  const [deletingAutomationIds, setDeletingAutomationIds] = useState<
    Set<string>
  >(() => new Set());

  const createdBy = useMemo(() => {
    const fullName = `${user?.userProfile?.firstName || ""} ${
      user?.userProfile?.lastName || ""
    }`.trim();

    return fullName || "Unknown user";
  }, [user?.userProfile?.firstName, user?.userProfile?.lastName]);

  const visibleAutomations = useMemo(() => {
    if (!workType) {
      return [];
    }

    return mapWorkTypeDefaultAutomationsToItems({
      workType,
      options: lookups.options,
      conditions,
    });
  }, [conditions, lookups.options, workType]);

  const automationTarget = useMemo<AutomationTarget | null>(() => {
    if (!workType) {
      return null;
    }

    return {
      type: "work",
      id: workType.id,
      title: "work item",
      entityLabel: "work item",
      hideTitleInPreview: true,
    };
  }, [workType]);

  if (!isVisible || !workType) {
    return null;
  }

  const handleOpenModal = () => {
    setSelectedTarget(automationTarget);
  };

  const handleCloseModal = () => {
    setSelectedTarget(null);
  };

  const handleAddAutomation = async (draft: AutomationDraft) => {
    if (
      isAddingAutomationRef.current ||
      !draft.conditionId ||
      !draft.actionId
    ) {
      return;
    }

    const orgId = getLinkedFirm()?.orgId || "";

    if (!orgId) {
      toast.error("Missing firm for automation");
      return;
    }

    const conditionValue = getAutomationConditionBodyValue({
      conditionKey: draft.conditionKey,
      conditionValue: draft.conditionValue,
      conditionExtraValue: draft.conditionExtraValue,
      workStatusOptions: lookups.options.workStatuses,
      subtaskStatusOptions: lookups.options.subtaskStatuses,
    });
    const actionValue = getAutomationActionBodyValue({
      actionKey: draft.actionKey,
      actionValue: draft.actionValue,
      actionExtraValue: draft.actionExtraValue,
      workStatusOptions: lookups.options.workStatuses,
      subtaskStatusOptions: lookups.options.subtaskStatuses,
    });
    const noOfDays =
      draft.conditionKey === AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE
        ? Number(draft.conditionValue)
        : undefined;

    isAddingAutomationRef.current = true;

    try {
      await addDefaultAutomation({
        orgId,
        workTypeId: workType.id,
        conditionId: draft.conditionId,
        actionId: draft.actionId,
        isActive: true,
        conditionValue,
        actionValue,
        noOfDays: Number.isFinite(noOfDays) ? noOfDays : undefined,
      }).unwrap();
      await onRefresh();
      setSelectedTarget(null);
      toast.success("Automation created successfully");
    } catch {
      toast.error("Failed to create automation");
    } finally {
      isAddingAutomationRef.current = false;
    }
  };

  const handleDeleteAutomation = async (automation: AutomationItem) => {
    const automationId = automation.id;

    if (deletingAutomationIdsRef.current.has(automationId)) {
      return;
    }

    deletingAutomationIdsRef.current.add(automationId);
    setDeletingAutomationIds(new Set(deletingAutomationIdsRef.current));

    try {
      await deleteDefaultAutomation(automationId).unwrap();
      await onRefresh();
      toast.success("Automation deleted successfully");
    } catch {
      toast.error("Failed to delete automation");
    } finally {
      deletingAutomationIdsRef.current.delete(automationId);
      setDeletingAutomationIds(new Set(deletingAutomationIdsRef.current));
    }
  };

  const renderAutomationList = () => {
    if (isLoadingConditions || lookups.isLoading) {
      return (
        <div
          className={
            "rounded-[11px] border border-dashed border-[#DCE2EA] p-7 text-center text-[13px] text-[#9097A8]"
          }>
          Loading automations...
        </div>
      );
    }

    if (isConditionsError) {
      return (
        <div
          className={
            "rounded-[11px] border border-red-100 bg-red-50 p-7 text-center text-[13px] text-red-700"
          }>
          Automations could not be loaded. Please try again.
        </div>
      );
    }

    if (visibleAutomations.length === 0) {
      return (
        <div
          className={
            "rounded-[11px] border border-dashed border-[#DCE2EA] p-7 text-center text-[13px] text-[#9097A8]"
          }>
          {EMPTY_STATE_TEXT}
        </div>
      );
    }

    return (
      <div className={"grid gap-3"}>
        {visibleAutomations.map((automation) => (
          <AutomationCard
            key={automation.id}
            automation={automation}
            workName={"work item"}
            options={lookups.options}
            isDeleting={deletingAutomationIds.has(automation.id)}
            hideWorkItemTitle
            sourceText={"Added from the work type template"}
            onDelete={handleDeleteAutomation}
          />
        ))}
      </div>
    );
  };

  return (
    <SettingsCard extraClasses={"p-6 mb-4"}>
      <div className={"mb-6 flex items-center justify-between gap-4"}>
        <h2 className={"m-0 text-[17px] font-semibold text-[#2B333C]"}>
          Automation
        </h2>

        <Button
          type={"button"}
          autoWidth
          className={
            "h-[32px] border-[#7c66f0] bg-[#7c66f0] px-4 text-sm font-semibold text-white hover:border-[#6d5ce0] hover:bg-[#6d5ce0]"
          }
          disabled={!automationTarget}
          loading={isAddingAutomation}
          onClick={handleOpenModal}>
          Add automation
        </Button>
      </div>

      {renderAutomationList()}

      <AddAutomationModal
        isOpen={Boolean(selectedTarget)}
        target={selectedTarget}
        createdBy={createdBy}
        lookups={lookups}
        isSaving={isAddingAutomation}
        onClose={handleCloseModal}
        onSave={handleAddAutomation}
      />
    </SettingsCard>
  );
};
